'use client';

import { useState, useEffect } from 'react';

/**
 * FilterPanel Component
 * Sidebar filters for property listings
 * 
 * @param {Object} props
 * @param {Object} props.filters - Current filter values
 * @param {function} props.onChange - Callback when filters change
 * @param {string} props.listingType - 'buy' | 'rent'
 */

// Filter options
const propertyTypes = [
    { value: "1", label: 'Villa' },
    { value: "2", label: 'Office Space' },
    { value: "3", label: 'Apartment' },
    { value: "4", label: 'Farmhouse' },
    { value: "5", label: 'Independent House' },
    { value: "7", label: 'PG/Hostel' },
    { value: "9", label: 'Warehouse' },
];

const facingOptions = [
    { value: 'north', label: 'North' },
    { value: 'east', label: 'East' },
    { value: 'west', label: 'West' },
    { value: 'south', label: 'South' },
    { value: 'north-east', label: 'North-East' },
    { value: 'north-west', label: 'North-West' },
    { value: 'south-east', label: 'South-East' },
    { value: 'south-west', label: 'South-West' },
];
const bhkOptions = [
    { value: '1', label: '1 BHK' },
    { value: '2', label: '2 BHK' },
    { value: '3', label: '3 BHK' },
    { value: '4', label: '4 BHK' },
    { value: '5+', label: '5+ BHK' },
];

const postedByOptions = [
    { value: 'seller', label: 'Owner' },
    { value: 'agent', label: 'Agent/Broker' },
    { value: 'builder', label: 'Builder' },
];

const furnishingOptions = [
    { value: 'furnished', label: 'Fully Furnished' },
    { value: 'semi', label: 'Semi Furnished' },
    { value: 'unfurnished', label: 'Unfurnished' },
];

const statusOptions = [
    { value: 'active', label: 'Ready to Move' },
    { value: 'pending', label: 'Available Later' },
];

const budgetRanges = {
    buy: [
        { min: 0, max: 30, label: 'Under ₹30 Lac' },
        { min: 30, max: 50, label: '₹30-50 Lac' },
        { min: 50, max: 75, label: '₹50-75 Lac' },
        { min: 75, max: 100, label: '₹75 Lac - ₹1 Cr' },
        { min: 100, max: 200, label: '₹1-2 Cr' },
        { min: 200, max: null, label: 'Above ₹2 Cr' },
    ],
    rent: [
        { min: 0, max: 10000, label: 'Under ₹10K' },
        { min: 10000, max: 20000, label: '₹10K-20K' },
        { min: 20000, max: 35000, label: '₹20K-35K' },
        { min: 35000, max: 50000, label: '₹35K-50K' },
        { min: 50000, max: null, label: 'Above ₹50K' },
    ],
    projects: [
        { min: 0, max: 40, label: 'Under ₹40 Lac' },
        { min: 40, max: 70, label: '₹40-70 Lac' },
        { min: 70, max: 100, label: '₹70 Lac - ₹1 Cr' },
        { min: 100, max: 200, label: '₹1-2 Cr' },
        { min: 200, max: 500, label: '₹2-5 Cr' },
        { min: 500, max: null, label: 'Above ₹5 Cr' },
    ],
    plots: [
        { min: 0, max: 10, label: 'Under ₹10 Lac' },
        { min: 10, max: 25, label: '₹10-25 Lac' },
        { min: 25, max: 50, label: '₹25-50 Lac' },
        { min: 50, max: 100, label: '₹50 Lac - ₹1 Cr' },
        { min: 100, max: 300, label: '₹1-3 Cr' },
        { min: 300, max: null, label: 'Above ₹3 Cr' },
    ],
    civil: [
        { min: 0, max: 5000, label: 'Under ₹5,000' },
        { min: 5000, max: 20000, label: '₹5,000 - ₹20,000' },
        { min: 20000, max: 50000, label: '₹20,000 - ₹50,000' },
        { min: 50000, max: 100000, label: '₹50,000 - ₹1 Lakh' },
        { min: 100000, max: null, label: 'Above ₹1 Lakh' },
    ],
};

const tenantPreferenceOptions = [
    { value: 'bachelor', label: 'Bachelors' },
    { value: 'family', label: 'Family' },
    { value: 'company', label: 'Company' },
];

const availabilityOptions = [
    { value: 'immediate', label: 'Immediate' },
    { value: 'pending', label: 'Available Later' },
];

const plotTypeOptions = [
    { value: "6", label: 'Plot' },
    { value: "8", label: 'Industrial Land' },
    { value: "10", label: 'Agricultural Land' },
];

const plotSizeOptions = [
    { value: '0-1000', label: 'Under 1000 sq.ft' },
    { value: '1000-2000', label: '1000-2000 sq.ft' },
    { value: '2000-3000', label: '2000-3000 sq.ft' },
    { value: '3000-5000', label: '3000-5000 sq.ft' },
    { value: '5000-10000', label: '5000-10000 sq.ft' },
    { value: '10000+', label: 'Above 10000 sq.ft' },
];

const civilServiceOptions = [
    { value: 'Cement', label: 'Cement' },
    { value: 'Steel', label: 'Steel' },
    { value: 'Concrete', label: 'Concrete' },
    { value: 'Aggregates', label: 'Aggregates' },
    { value: 'Bricks', label: 'Bricks' },
    { value: 'Flooring & Tiling', label: 'Flooring & Tiling' },
    { value: 'Plastering & Wall Finish', label: 'Plastering & Wall Finish' },
    { value: 'Paints & Coatings', label: 'Paints & Coatings' },
    { value: 'Doors & Windows', label: 'Doors & Windows' },
];

const experienceOptions = [
    { value: '1-3', label: '1-3 Years' },
    { value: '3-5', label: '3-5 Years' },
    { value: '5+', label: '5+ Years' },
];

const approvalOptions = [
    { value: 'cmda', label: 'CMDA Approved' },
    { value: 'dtcp', label: 'DTCP Approved' },
    // { value: 'rera', label: 'RERA Registered' },
    // { value: 'panchayat', label: 'Panchayat Approved' },
];

const FilterPanel = ({
    filters = {},
    onChange,
    listingType = 'buy',
    isMobile = false,
    onClose
}) => {
    const [localFilters, setLocalFilters] = useState(filters);

    // Sync localFilters state with parent filters prop
    useEffect(() => {
        setLocalFilters(filters || {});
    }, [filters]);

    const handleFilterChange = (key, value) => {
        const newFilters = { ...localFilters };

        if (Array.isArray(newFilters[key])) {
            if (newFilters[key].includes(value)) {
                newFilters[key] = newFilters[key].filter(v => v !== value);
            } else {
                newFilters[key] = [...newFilters[key], value];
            }
        } else {
            newFilters[key] = value;
        }

        setLocalFilters(newFilters);
        if (onChange) onChange(newFilters);
    };

    const toggleArrayFilter = (key, value) => {
        const current = localFilters[key] || [];
        const updated = current.includes(value)
            ? current.filter(v => v !== value)
            : [...current, value];

        const newFilters = { ...localFilters, [key]: updated };
        setLocalFilters(newFilters);
        if (onChange) onChange(newFilters);
    };

    const clearFilters = () => {
        const cleared = {};
        setLocalFilters(cleared);
        if (onChange) onChange(cleared);
    };

    const activeFilterCount = Object.values(localFilters).filter(
        v => v && (Array.isArray(v) ? v.length > 0 : true)
    ).length;

    return (
        <div className={`filter-sidebar ${isMobile ? 'p-0' : ''}`}>
            {isMobile && (
                <div className="sticky top-0 z-50 bg-white border-b border-gray-200 px-2.5 py-1.5 flex items-center justify-between gap-1 shadow-xs">
                    <button
                        onClick={onClose}
                        className="text-gray-600 hover:text-gray-900 font-medium text-[11px] flex items-center gap-1"
                        aria-label="Close filter drawer"
                    >
                        ✕ Close
                    </button>

                    <div className="flex items-center gap-1.5">
                        <button
                            onClick={clearFilters}
                            className="text-red-500 hover:text-red-700 font-medium text-[11px] px-1.5 py-0.5 whitespace-nowrap"
                        >
                            Reset
                        </button>

                        <button
                            onClick={onClose}
                            className="bg-primary hover:bg-primary-dark text-white px-2.5 py-1 rounded-md font-medium text-[11px] whitespace-nowrap shadow-xs"
                        >
                            Apply
                        </button>
                    </div>
                </div>
            )}

            <div className={isMobile ? "p-2.5 space-y-2.5" : "space-y-5"}>
                    {/* Property Type (Hidden for Civil, Plots, and Projects) */ }
                {listingType !== 'civil' && listingType !== 'plots' && listingType !== 'projects' && (
                    <div className="filter-section">
                        <h4 className={`filter-title ${isMobile ? 'text-[10px] mb-1' : 'text-xs mb-2'} font-semibold text-gray-400 uppercase tracking-wider`}>
                            Property Type
                        </h4>
                        <div className={`flex flex-wrap ${isMobile ? 'gap-1' : 'gap-2'}`}>
                            {propertyTypes.map((type) => (
                                <button
                                    key={type.value}
                                    onClick={() => toggleArrayFilter('propertyType', type.value)}
                                    className={`filter-chip ${isMobile ? 'text-[11px] px-2 py-0.5 leading-tight' : 'text-sm px-3 py-1.5'} rounded-lg border-2 transition-all duration-200 ${(localFilters.propertyType || []).includes(type.value)
                                        ? 'border-primary bg-primary/10 text-primary font-semibold shadow-xs'
                                        : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
                                        }`}
                                >
                                    {type.label}
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                {/* Facing (Hidden for Civil) */}
                {listingType !== 'civil' && (
                    <div className="filter-section">
                        <h4 className={`filter-title ${isMobile ? 'text-[10px] mb-1' : 'text-xs mb-2'} font-semibold text-gray-400 uppercase tracking-wider`}>
                            Facing
                        </h4>
                        <div className={`flex flex-wrap ${isMobile ? 'gap-1' : 'gap-2'}`}>
                            {facingOptions.map((facing) => (
                                <button
                                    key={facing.value}
                                    onClick={() => toggleArrayFilter('facing', facing.value)}
                                    className={`filter-chip ${isMobile ? 'text-[11px] px-2 py-0.5 leading-tight' : 'text-sm px-3 py-1.5'} rounded-lg border-2 transition-all duration-200 ${(localFilters.facing || []).includes(facing.value)
                                        ? 'border-primary bg-primary/10 text-primary font-semibold shadow-xs'
                                        : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
                                        }`}
                                >
                                    {facing.label}
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                {/* Service Type (For Civil Only) */}
                {listingType === 'civil' && (
                    <div className="filter-section">
                        <h4 className={`filter-title ${isMobile ? 'text-[10px] mb-1' : 'text-xs mb-2'} font-semibold text-gray-400 uppercase tracking-wider`}>
                            Service Type
                        </h4>
                        <div className={`flex flex-wrap ${isMobile ? 'gap-1' : 'gap-2'}`}>
                            {civilServiceOptions.map((type) => (
                                <button
                                    key={type.value}
                                    onClick={() => toggleArrayFilter('serviceType', type.value)}
                                    className={`filter-chip ${isMobile ? 'text-[11px] px-2 py-0.5 leading-tight' : 'text-sm px-3 py-1.5'} rounded-lg border-2 transition-all duration-200 ${(localFilters.serviceType || []).includes(type.value)
                                        ? 'border-primary bg-primary/10 text-primary font-semibold shadow-xs'
                                        : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
                                        }`}
                                >
                                    {type.label}
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                {/* Experience (For Civil Only) */}
                {listingType === 'civil' && (
                    <div className="filter-section">
                        <h4 className={`filter-title ${isMobile ? 'text-[10px] mb-1' : 'text-xs mb-2'} font-semibold text-gray-400 uppercase tracking-wider`}>
                            Experience
                        </h4>
                        <div className={`flex flex-wrap ${isMobile ? 'gap-1' : 'gap-2'}`}>
                            {experienceOptions.map((opt) => (
                                <button
                                    key={opt.value}
                                    onClick={() => toggleArrayFilter('experience', opt.value)}
                                    className={`filter-chip ${isMobile ? 'text-[11px] px-2 py-0.5 leading-tight' : 'text-sm px-3 py-1.5'} rounded-lg border-2 transition-all duration-200 ${(localFilters.experience || []).includes(opt.value)
                                        ? 'border-primary bg-primary/10 text-primary font-semibold shadow-xs'
                                        : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
                                        }`}
                                >
                                    {opt.label}
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                {/* BHK Type (Hidden for Civil and Plots) */}
                {listingType !== 'civil' && listingType !== 'plots' && (
                    <div className="filter-section">
                        <h4 className={`filter-title ${isMobile ? 'text-[10px] mb-1' : 'text-xs mb-2'} font-semibold text-gray-400 uppercase tracking-wider`}>
                            BHK Type
                        </h4>
                        <div className={`flex flex-wrap ${isMobile ? 'gap-1' : 'gap-2'}`}>
                            {bhkOptions.map((bhk) => (
                                <button
                                    key={bhk.value}
                                    onClick={() => toggleArrayFilter('bhk', bhk.value)}
                                    className={`filter-chip ${isMobile ? 'text-[11px] px-2 py-0.5 leading-tight' : 'text-sm px-3 py-1.5'} rounded-lg border-2 transition-all duration-200 ${(localFilters.bhk || []).includes(bhk.value)
                                        ? 'border-primary bg-primary/10 text-primary font-semibold shadow-xs'
                                        : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
                                        }`}
                                >
                                    {bhk.label}
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                {/* Plot Type (For Plots Only) */}
                {listingType === 'plots' && (
                    <div className="filter-section">
                        <h4 className={`filter-title ${isMobile ? 'text-[10px] mb-1' : 'text-xs mb-2'} font-semibold text-gray-400 uppercase tracking-wider`}>
                            Plot Type
                        </h4>
                        <div className={`flex flex-wrap ${isMobile ? 'gap-1' : 'gap-2'}`}>
                            {plotTypeOptions.map((opt) => (
                                <button
                                    key={opt.value}
                                    onClick={() => toggleArrayFilter('plotType', opt.value)}
                                    className={`filter-chip ${isMobile ? 'text-[11px] px-2 py-0.5 leading-tight' : 'text-sm px-3 py-1.5'} rounded-lg border-2 transition-all duration-200 ${(localFilters.plotType || []).includes(opt.value)
                                        ? 'border-primary bg-primary/10 text-primary font-semibold shadow-xs'
                                        : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
                                        }`}
                                >
                                    {opt.label}
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                {/* Plot Size (For Plots Only) */}
                {listingType === 'plots' && (
                    <div className="filter-section">
                        <h4 className={`filter-title ${isMobile ? 'text-[10px] mb-1' : 'text-xs mb-2'} font-semibold text-gray-400 uppercase tracking-wider`}>
                            Plot Size
                        </h4>
                        <div className={`flex flex-wrap ${isMobile ? 'gap-1' : 'gap-2'}`}>
                            {plotSizeOptions.map((opt) => (
                                <button
                                    key={opt.value}
                                    onClick={() => toggleArrayFilter('plotSize', opt.value)}
                                    className={`filter-chip ${isMobile ? 'text-[11px] px-2 py-0.5 leading-tight' : 'text-sm px-3 py-1.5'} rounded-lg border-2 transition-all duration-200 ${(localFilters.plotSize || []).includes(opt.value)
                                        ? 'border-primary bg-primary/10 text-primary font-semibold shadow-xs'
                                        : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
                                        }`}
                                >
                                    {opt.label}
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                {/* Budget - Box Style (Hidden for Civil) */}
                {listingType !== 'civil' && (
                    <div className="filter-section">
                        <div className={`flex justify-between items-center ${isMobile ? 'mb-1' : 'mb-2'}`}>
                            <h4 className={`filter-title ${isMobile ? 'text-[10px]' : 'text-xs'} font-semibold text-gray-400 uppercase tracking-wider`}>
                                Budget
                            </h4>
                            {localFilters.budgetMin !== undefined && (
                                <button
                                    onClick={() => {
                                        const newFilters = { ...localFilters };
                                        delete newFilters.budgetMin;
                                        delete newFilters.budgetMax;
                                        setLocalFilters(newFilters);
                                        if (onChange) onChange(newFilters);
                                    }}
                                    className="text-xs text-red-500 hover:text-red-700 font-medium"
                                >
                                    Clear
                                </button>
                            )}
                        </div>
                        <div className={`grid grid-cols-2 ${isMobile ? 'gap-1.5' : 'gap-2'}`}>
                            {(budgetRanges[listingType] || budgetRanges.buy).map((range, idx) => (
                                <button
                                    key={idx}
                                    onClick={() => {
                                        const isAlreadySelected = localFilters.budgetMin === range.min && localFilters.budgetMax === range.max;
                                        const newFilters = { ...localFilters };
                                        if (isAlreadySelected) {
                                            delete newFilters.budgetMin;
                                            delete newFilters.budgetMax;
                                        } else {
                                            newFilters.budgetMin = range.min;
                                            newFilters.budgetMax = range.max;
                                        }
                                        setLocalFilters(newFilters);
                                        if (onChange) onChange(newFilters);
                                    }}
                                    className={`budget-box ${isMobile ? 'text-xs px-2.5 py-2 font-medium' : 'text-sm px-3 py-2.5'} rounded-lg border-2 transition-all duration-200 text-center ${localFilters.budgetMin === range.min && localFilters.budgetMax === range.max
                                        ? 'border-primary bg-primary/10 text-primary font-semibold shadow-xs'
                                        : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300 hover:bg-gray-50'
                                        }`}
                                >
                                    {range.label}
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                {/* Approvals (For Plots Only) */}
                {listingType === 'plots' && (
                    <div className="filter-section">
                        <h4 className={`filter-title ${isMobile ? 'text-[10px] mb-1' : 'text-xs mb-2'} font-semibold text-gray-400 uppercase tracking-wider`}>
                            Approvals
                        </h4>
                        <div className={`flex flex-wrap ${isMobile ? 'gap-1' : 'gap-2'}`}>
                            {approvalOptions.map((opt) => (
                                <button
                                    key={opt.value}
                                    onClick={() => toggleArrayFilter('approval', opt.value)}
                                    className={`filter-chip ${isMobile ? 'text-[11px] px-2 py-0.5 leading-tight' : 'text-sm px-3 py-1.5'} rounded-lg border-2 transition-all duration-200 ${(localFilters.approval || []).includes(opt.value)
                                        ? 'border-primary bg-primary/10 text-primary font-semibold shadow-xs'
                                        : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
                                        }`}
                                >
                                    {opt.label}
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                {/* Furnishing (Hidden for Plots and Civil) */}
                {listingType !== 'plots' && listingType !== 'civil' && (
                    <div className="filter-section">
                        <h4 className={`filter-title ${isMobile ? 'text-[10px] mb-1' : 'text-xs mb-2'} font-semibold text-gray-400 uppercase tracking-wider`}>
                            Furnishing
                        </h4>
                        <div className={`flex flex-wrap ${isMobile ? 'gap-1' : 'gap-2'}`}>
                            {furnishingOptions.map((opt) => (
                                <button
                                    key={opt.value}
                                    onClick={() => toggleArrayFilter('furnishing', opt.value)}
                                    className={`filter-chip ${isMobile ? 'text-[11px] px-2 py-0.5 leading-tight' : 'text-sm px-3 py-1.5'} rounded-lg border-2 transition-all duration-200 ${(localFilters.furnishing || []).includes(opt.value)
                                        ? 'border-primary bg-primary/10 text-primary font-semibold shadow-xs'
                                        : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
                                        }`}
                                >
                                    {opt.label}
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                {/* Tenant Preference (For Rent Only) */}
                {listingType === 'rent' && (
                    <div className="filter-section">
                        <h4 className={`filter-title ${isMobile ? 'text-[10px] mb-1' : 'text-xs mb-2'} font-semibold text-gray-400 uppercase tracking-wider`}>
                            Tenant Preference
                        </h4>
                        <div className={`flex flex-wrap ${isMobile ? 'gap-1' : 'gap-2'}`}>
                            {tenantPreferenceOptions.map((opt) => (
                                <button
                                    key={opt.value}
                                    onClick={() => toggleArrayFilter('tenantPreference', opt.value)}
                                    className={`filter-chip ${isMobile ? 'text-[11px] px-2 py-0.5 leading-tight' : 'text-sm px-3 py-1.5'} rounded-lg border-2 transition-all duration-200 ${(localFilters.tenantPreference || []).includes(opt.value)
                                        ? 'border-primary bg-primary/10 text-primary font-semibold shadow-xs'
                                        : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
                                        }`}
                                >
                                    {opt.label}
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                {/* Availability (For Rent Only) */}
                {listingType === 'rent' && (
                    <div className="filter-section">
                        <h4 className={`filter-title ${isMobile ? 'text-[10px] mb-1' : 'text-xs mb-2'} font-semibold text-gray-400 uppercase tracking-wider`}>
                            Availability
                        </h4>
                        <div className={`flex flex-wrap ${isMobile ? 'gap-1' : 'gap-2'}`}>
                            {availabilityOptions.map((opt) => (
                                <button
                                    key={opt.value}
                                    onClick={() => toggleArrayFilter('availability', opt.value)}
                                    className={`filter-chip ${isMobile ? 'text-[11px] px-2 py-0.5 leading-tight' : 'text-sm px-3 py-1.5'} rounded-lg border-2 transition-all duration-200 ${(localFilters.availability || []).includes(opt.value)
                                        ? 'border-primary bg-primary/10 text-primary font-semibold shadow-xs'
                                        : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
                                        }`}
                                >
                                    {opt.label}
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                {/* Status (For Buy & Projects Only) */}
                {(listingType === 'buy' || listingType === 'projects') && (
                    <div className="filter-section">
                        <h4 className={`filter-title ${isMobile ? 'text-[10px] mb-1' : 'text-xs mb-2'} font-semibold text-gray-400 uppercase tracking-wider`}>
                            Status
                        </h4>
                        <div className={`flex flex-wrap ${isMobile ? 'gap-1' : 'gap-2'}`}>
                            {statusOptions.map((opt) => (
                                <button
                                    key={opt.value}
                                    onClick={() => toggleArrayFilter('status', opt.value)}
                                    className={`filter-chip ${isMobile ? 'text-[11px] px-2 py-0.5 leading-tight' : 'text-sm px-3 py-1.5'} rounded-lg border-2 transition-all duration-200 ${(localFilters.status || []).includes(opt.value)
                                        ? 'border-primary bg-primary/10 text-primary font-semibold shadow-xs'
                                        : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
                                        }`}
                                >
                                    {opt.label}
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                {/* Posted By (Hidden for Civil) */}
                {listingType !== 'civil' && (
                    <div className="filter-section">
                        <h4 className={`filter-title ${isMobile ? 'text-[10px] mb-1' : 'text-xs mb-2'} font-semibold text-gray-400 uppercase tracking-wider`}>
                            Posted By
                        </h4>
                        <div className={`flex flex-wrap ${isMobile ? 'gap-1' : 'gap-2'}`}>
                            {postedByOptions.map((opt) => (
                                <button
                                    key={opt.value}
                                    onClick={() => toggleArrayFilter('postedBy', opt.value)}
                                    className={`filter-chip ${isMobile ? 'text-[11px] px-2 py-0.5 leading-tight' : 'text-sm px-3 py-1.5'} rounded-lg border-2 transition-all duration-200 ${(localFilters.postedBy || []).includes(opt.value)
                                        ? 'border-primary bg-primary/10 text-primary font-semibold shadow-xs'
                                        : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
                                        }`}
                                >
                                    {opt.label}
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                {/* Active Filters Summary */}
                {activeFilterCount > 0 && (
                    <div className="pt-3 border-t border-gray-100">
                        <button
                            onClick={clearFilters}
                            className="text-xs text-red-500 font-medium hover:text-red-600 transition-colors"
                        >
                            Clear All Filters ({activeFilterCount})
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default FilterPanel;